/**
 * Filtros e selos do Comercial (portados da DEMO-03: filtros.tsx, sessao.tsx,
 * componentes.tsx): situação da Oportunidade com seleção múltipla guardada SÓ na
 * memória da página (nova sessão volta para "Em aberto"), período com a data
 * considerada escrita, parceiro comercial, selo de TIPO, selo do Registro com
 * alerta progressivo e o alerta de prioridade (a cor nunca é o único meio).
 */
import { createContext, useId, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Icone } from '../componentes/icones.tsx';
import { NIVEIS, PRESETS, TIPOS, dataBr, dataEncerramentoRegistro, intervalo, periodoInicial, statusRegistro, type Estagio, type FaixasRegistro, type NivelRegistro, type Periodo, type Preset, type RegistroBase, type Tipo } from '../../../backend/src/compartilhado/oportunidades.ts';
import { CampoData, hojeIso } from './CampoData.tsx';
import type { Tom } from './ui.tsx';

/* ---------- Situação da Oportunidade (preferência da sessão de navegação) ---------- */
export type Situacao = 'aberta' | 'ganha' | 'ganha_parcialmente' | 'perdida' | 'cancelada';
export const SITUACOES: { id: Situacao; nome: string }[] = [
  { id: 'aberta', nome: 'Em aberto' },
  { id: 'ganha', nome: 'Ganha' },
  { id: 'ganha_parcialmente', nome: 'Ganha parcialmente' },
  { id: 'perdida', nome: 'Perdida' },
  { id: 'cancelada', nome: 'Cancelada' },
];
export const SITUACAO_PADRAO: Situacao[] = ['aberta'];
const ContextoSituacao = createContext<{ situacoes: Situacao[]; setSituacoes: (s: Situacao[]) => void } | null>(null);
export function ProvedorSituacao({ children }: { children: ReactNode }) {
  const [situacoes, setSituacoes] = useState<Situacao[]>(SITUACAO_PADRAO);
  return <ContextoSituacao.Provider value={{ situacoes, setSituacoes }}>{children}</ContextoSituacao.Provider>;
}
export function useSituacoes() {
  const c = useContext(ContextoSituacao);
  if (!c) throw new Error('ProvedorSituacao ausente');
  return c;
}

/**
 * Filtro de seleção múltipla — padrão A do pacote 2 (P2-09, D13): checkbox à esquerda, resumo no botão
 * ("Em aberto", "2 status selecionados", "Todos"), rodapé com Todas · Restaurar padrão · Aplicar. A escolha só vale
 * ao clicar em Aplicar; fechar (clique fora ou Esc) descarta o rascunho e mantém a seleção anterior.
 */
export function FiltroMultiplo<T extends string>({ rotulo, opcoes, valor, padrao, aoAplicar, teste, prefixo, rotuloGrupo }: { rotulo: string; opcoes: { id: T; nome: string }[]; valor: T[]; padrao: T[]; aoAplicar: (v: T[]) => void; teste: string; prefixo: string; rotuloGrupo: string }) {
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState<T[]>(valor);
  const ref = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const [aDireita, setADireita] = useState(false);
  const idRotulo = useId();
  // CA-V3/V4: perto da borda direita (ex.: 1024 px), o painel abre alinhado à direita do botão em vez de sair da tela
  useLayoutEffect(() => {
    if (!aberto) { setADireita(false); return; }
    const r = painel.current?.getBoundingClientRect();
    if (r && r.right > document.documentElement.clientWidth - 8) setADireita(true);
  }, [aberto]);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setAberto(false);
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);
  const nomes = opcoes.filter((o) => valor.includes(o.id)).map((o) => o.nome);
  // D1 (pacote 3): aplicar sem nenhuma opção = sem restrição nesse critério; o resumo mostra "Todos"
  const todas = opcoes.map((o) => o.id);
  const resumo = valor.length === 0 || valor.length === opcoes.length ? 'Todos' : valor.length === 1 ? nomes[0] : `${valor.length} status selecionados`;
  const ordenar = (v: T[]) => opcoes.map((o) => o.id).filter((x) => v.includes(x));
  const aplicar = (v: T[]) => aoAplicar(v.length ? ordenar(v) : todas);
  // visão salva antiga com o critério vazio: passa a valer como "sem restrição"
  useEffect(() => {
    if (valor.length === 0 && opcoes.length) aoAplicar(todas);
  }, [valor.length, opcoes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const tudoMarcado = rascunho.length === opcoes.length;
  const abrir = () => (setRascunho(valor), setAberto(!aberto));
  return (
    <div className="filtro-situacao filtro-multiplo" ref={ref} onKeyDown={(e) => e.key === 'Escape' && aberto && (e.stopPropagation(), setAberto(false))}>
      <span className="rotulo-filtro" id={idRotulo}>
        {rotulo}
      </span>
      <button type="button" className="seletor-multiplo" aria-haspopup="true" aria-expanded={aberto} aria-labelledby={`${idRotulo} ${idRotulo}-resumo`} title={nomes.join(', ') || undefined} onClick={abrir} data-teste={teste}>
        <span id={`${idRotulo}-resumo`} data-teste={`resumo-${prefixo}`}>
          {resumo}
        </span>
        <span aria-hidden>▾</span>
      </button>
      {aberto && (
        <div ref={painel} className={`painel-multiplo${aDireita ? ' a-direita' : ''}`} role="group" aria-label={rotuloGrupo}>
          {opcoes.map((o) => (
            <label key={o.id} className="opcao">
              <input type="checkbox" checked={rascunho.includes(o.id)} onChange={() => setRascunho(ordenar(rascunho.includes(o.id) ? rascunho.filter((x) => x !== o.id) : [...rascunho, o.id]))} data-teste={`${prefixo}-${o.id}`} /> <span>{o.nome}</span>
            </label>
          ))}
          <div className="rodape-multi">
            {/* P3-07: alterna conforme a seleção em edição; só afeta este critério e só vale ao clicar em Aplicar */}
            <button type="button" className="botao-link" onClick={() => setRascunho(tudoMarcado ? [] : todas)} data-teste={`${prefixo}-todas`}>
              {tudoMarcado ? 'Desmarcar todas' : 'Selecionar todas'}
            </button>
            <button type="button" className="botao-link" onClick={() => setRascunho(padrao)} data-teste={`${prefixo}-padrao`}>
              Restaurar padrão
            </button>
            <button type="button" className="botao-primario pequeno" onClick={() => (aplicar(rascunho), setAberto(false))} data-teste={`${prefixo}-aplicar`}>
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Status das Oportunidades (P2-14: antes "Situação"): padrão A, compartilhado entre Kanban, Lista e a aba do Cliente. */
export function FiltroSituacao() {
  const { situacoes, setSituacoes } = useSituacoes();
  return <FiltroMultiplo rotulo="Status" opcoes={SITUACOES} valor={situacoes} padrao={SITUACAO_PADRAO} aoAplicar={setSituacoes} teste="filtro-situacao-oportunidade" prefixo="situacao" rotuloGrupo="Status da Oportunidade" />;
}

/* ---------- Período (cada tela diz QUAL data está sendo filtrada) ---------- */
export function usePeriodo<T extends string>(dataInicial: T) {
  const [periodo, setPeriodo] = useState<Periodo>(() => periodoInicial(hojeIso()));
  const [data, setData] = useState<T>(dataInicial);
  return { periodo, setPeriodo, data, setData };
}
export function FiltroPeriodo<T extends string>({ periodo, setPeriodo, datas, data, setData, semData, teste = 'periodo' }: { periodo: Periodo; setPeriodo: (p: Periodo) => void; datas: { id: T; rotulo: string }[]; data: T; setData: (d: T) => void; semData?: boolean; /** data-teste do seletor de período */ teste?: string }) {
  const ano = Number(hojeIso().slice(0, 4));
  const anos = [ano - 1, ano, ano + 1];
  return (
    <>
      {!semData && (
        <label>
          Data considerada
          <select value={data} onChange={(e) => setData(e.target.value as T)} data-teste="data-considerada">
            {datas.map((x) => (
              <option key={x.id} value={x.id}>
                {x.rotulo}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        Período
        <select value={periodo.preset} onChange={(e) => setPeriodo({ ...periodo, preset: e.target.value as Preset })} data-teste={teste}>
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.rotulo}
            </option>
          ))}
        </select>
      </label>
      {periodo.preset === 'trimestre' && (
        <label>
          Trimestre
          <select value={periodo.trimestre} onChange={(e) => setPeriodo({ ...periodo, trimestre: Number(e.target.value) as 1 | 2 | 3 | 4 })} data-teste="trimestre">
            {[1, 2, 3, 4].map((t) => (
              <option key={t} value={t}>
                {t}º trimestre
              </option>
            ))}
          </select>
        </label>
      )}
      {(periodo.preset === 'trimestre' || periodo.preset === 'ano') && (
        <label>
          Ano
          <select value={periodo.ano} onChange={(e) => setPeriodo({ ...periodo, ano: Number(e.target.value) })} data-teste="ano">
            {anos.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </label>
      )}
      {periodo.preset === 'personalizado' && (
        <>
          <CampoData rotulo="De" valor={periodo.de} aoMudar={(x) => x && setPeriodo({ ...periodo, de: x })} max={periodo.ate || undefined} teste="de" obrigatorio />
          <CampoData rotulo="Até" valor={periodo.ate} aoMudar={(x) => x && setPeriodo({ ...periodo, ate: x })} min={periodo.de || undefined} teste="ate" obrigatorio />
        </>
      )}
    </>
  );
}
/* ---------- Barra de filtros progressiva (Fase 1, F1-03) ----------
 * Estado inicial compacto: só os filtros fixos da tela. "Adicionar filtro" escolhe o campo; cada critério ativo vira
 * um chip de altura única ("Campo: valor", texto completo na dica) com × para remover; clicar no chip reabre o
 * controle. "Limpar filtros" à direita. Sem faixa "Filtrando por": o período aparece no próprio chip/controle. */
export interface FiltroOpcional {
  id: string;
  rotulo: string;
  ativo: boolean;
  resumo: string;
  controle: ReactNode;
  limpar: () => void;
}
export function BarraFiltros({ teste, fixos, opcionais = [], aoLimpar, direita }: { teste: string; fixos?: ReactNode; opcionais?: FiltroOpcional[]; aoLimpar?: () => void; direita?: ReactNode }) {
  const [mostrados, setMostrados] = useState<string[]>([]);
  const [aberto, setAberto] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (ref.current && !ref.current.querySelector('.popover-filtro, .menu-adicionar')?.contains(alvo) && !(alvo as Element).closest?.('[data-abre-filtro]')) setAberto(null);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(null);
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => (document.removeEventListener('mousedown', fora), document.removeEventListener('keydown', esc));
  }, [aberto]);
  const chips = opcionais.filter((o) => o.ativo || mostrados.includes(o.id));
  const disponiveis = opcionais.filter((o) => !chips.includes(o));
  const popover = (o: FiltroOpcional) => (
    <div className="popover-filtro" role="dialog" aria-label={`Filtro: ${o.rotulo}`} data-teste={`popover-filtro-${o.id}`}>
      <div className="popover-campos">{o.controle}</div>
      <div className="popover-acoes">
        <button type="button" className="botao-secundario pequeno" onClick={() => setAberto(null)} data-teste={`fechar-filtro-${o.id}`}>
          Fechar
        </button>
      </div>
    </div>
  );
  return (
    <div className="barra-filtros" ref={ref} data-teste={`filtros-${teste}`}>
      <span className="icone-filtros" aria-hidden="true">
        <Icone nome="filtro" />
      </span>
      {fixos}
      {disponiveis.length > 0 && (
        <div className="ancora-filtro">
          <button type="button" className="botao-secundario adicionar-filtro" aria-expanded={aberto === '+'} data-abre-filtro onClick={() => setAberto(aberto === '+' ? null : '+')} data-teste={`${teste}-adicionar-filtro`}>
            <Icone nome="mais" /> Adicionar filtro
          </button>
          {aberto === '+' && (
            <div className="menu-adicionar" role="menu" aria-label="Adicionar filtro">
              {disponiveis.map((o) => (
                <button key={o.id} type="button" role="menuitem" onClick={() => (setMostrados([...mostrados, o.id]), setAberto(o.id))} data-teste={`filtro-opcao-${o.id}`}>
                  {o.rotulo}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="filtros-direita">
        {aoLimpar && (
          <button type="button" className="botao-secundario" onClick={() => (aoLimpar(), setMostrados([]), setAberto(null))} data-teste={`limpar-filtros-${teste}`}>
            Limpar filtros
          </button>
        )}
        {direita}
      </div>
      {chips.length > 0 && (
        <div className="chips-filtros">
          {chips.map((o) => {
            const texto = `${o.rotulo}: ${o.ativo ? o.resumo : 'escolha…'}`;
            return (
              <div key={o.id} className="ancora-filtro">
                <span className={`chip-filtro${aberto === o.id ? ' aberto' : ''}`} data-teste={`chip-filtro-${o.id}`}>
                  <button type="button" className="chip-texto" title={texto} aria-expanded={aberto === o.id} data-abre-filtro onClick={() => setAberto(aberto === o.id ? null : o.id)}>
                    {o.rotulo}: <b>{o.ativo ? o.resumo : 'escolha…'}</b>
                  </button>
                  <button type="button" className="chip-remover" aria-label={`Remover filtro ${o.rotulo}`} title={`Remover filtro ${o.rotulo}`} onClick={() => (o.limpar(), setMostrados(mostrados.filter((x) => x !== o.id)), setAberto(null))} data-teste={`remover-filtro-${o.id}`}>
                    <Icone nome="fechar" />
                  </button>
                </span>
                {aberto === o.id && popover(o)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Filtro de período como critério da barra: data considerada + período no mesmo controle; resumo com as datas. */
export function filtroPeriodo<T extends string>(per: { periodo: Periodo; setPeriodo: (p: Periodo) => void; data: T; setData: (d: T) => void }, datas: { id: T; rotulo: string; curto?: string }[], dataPadrao: T): FiltroOpcional {
  const d = datas.find((x) => x.id === per.data) ?? datas[0];
  return {
    id: 'periodo',
    rotulo: `Período (${(d.curto ?? d.rotulo).toLowerCase()})`,
    ativo: per.periodo.preset !== 'tudo',
    resumo: intervalo(per.periodo, hojeIso()).rotulo,
    controle: <FiltroPeriodo periodo={per.periodo} setPeriodo={per.setPeriodo} datas={datas} data={per.data} setData={per.setData} />,
    limpar: () => (per.setPeriodo(periodoInicial(hojeIso())), per.setData(dataPadrao)),
  };
}

/** Filtro de lista simples (select) como critério da barra. */
export function filtroSelecao(id: string, rotulo: string, valor: string, setValor: (v: string) => void, opcoes: { id: string; nome: string }[], teste: string, todos = 'Todos'): FiltroOpcional {
  return {
    id,
    rotulo,
    ativo: valor !== '',
    resumo: opcoes.find((o) => o.id === valor)?.nome ?? valor,
    controle: (
      <label>
        {rotulo}
        <select value={valor} onChange={(e) => setValor(e.target.value)} data-teste={teste}>
          <option value="">{todos}</option>
          {opcoes.map((o) => (
            <option key={o.id} value={o.id}>
              {o.nome}
            </option>
          ))}
        </select>
      </label>
    ),
    limpar: () => setValor(''),
  };
}

/** Critério opcional com várias escolhas (ex.: UF), aplicado ao marcar. */
export function filtroVarios(id: string, rotulo: string, valor: string[], setValor: (v: string[]) => void, opcoes: (string | { id: string; nome: string })[], teste: string): FiltroOpcional {
  const ops = opcoes.map((o) => (typeof o === 'string' ? { id: o, nome: o } : o));
  return {
    id,
    rotulo,
    ativo: valor.length > 0,
    resumo: ops.filter((o) => valor.includes(o.id)).map((o) => o.nome).join(', '),
    controle: (
      <fieldset className={`opcoes-varios${ops.every((o) => o.nome.length <= 4) ? ' curtas' : ''}`} data-teste={teste}>
        <legend>{rotulo}</legend>
        {ops.map((o) => (
          <label key={o.id} className="opcao">
            <input type="checkbox" checked={valor.includes(o.id)} onChange={(e) => setValor(e.target.checked ? [...valor, o.id] : valor.filter((x) => x !== o.id))} data-teste={`${teste}-${o.id}`} />
            {o.nome}
          </label>
        ))}
      </fieldset>
    ),
    limpar: () => setValor([]),
  };
}

/**
 * Critério opcional com várias escolhas e botão "Aplicar" (revisão 12/10: Status na aba Pedidos do Cliente, no mesmo
 * padrão do filtro múltiplo). Marcar só altera o rascunho; "Fechar" ou clicar fora mantém a seleção anterior.
 */
export function filtroVariosComAplicar(id: string, rotulo: string, valor: string[], setValor: (v: string[]) => void, opcoes: { id: string; nome: string }[], teste: string): FiltroOpcional {
  return {
    id,
    rotulo,
    ativo: valor.length > 0,
    resumo: opcoes.filter((o) => valor.includes(o.id)).map((o) => o.nome).join(', '),
    controle: <RascunhoVarios rotulo={rotulo} valor={valor} aoAplicar={setValor} opcoes={opcoes} teste={teste} />,
    limpar: () => setValor([]),
  };
}
function RascunhoVarios({ rotulo, valor, aoAplicar, opcoes, teste }: { rotulo: string; valor: string[]; aoAplicar: (v: string[]) => void; opcoes: { id: string; nome: string }[]; teste: string }) {
  const [rascunho, setRascunho] = useState<string[]>(valor);
  const ordenar = (v: string[]) => opcoes.map((o) => o.id).filter((x) => v.includes(x));
  const mudou = ordenar(rascunho).join('|') !== ordenar(valor).join('|');
  return (
    <fieldset className="opcoes-varios" data-teste={teste}>
      <legend>{rotulo}</legend>
      {opcoes.map((o) => (
        <label key={o.id} className="opcao">
          <input type="checkbox" checked={rascunho.includes(o.id)} onChange={(e) => setRascunho(e.target.checked ? [...rascunho, o.id] : rascunho.filter((x) => x !== o.id))} data-teste={`${teste}-${o.id}`} />
          {o.nome}
        </label>
      ))}
      <div className="acoes-rascunho">
        <button type="button" className="botao-link" onClick={() => setRascunho(rascunho.length === opcoes.length ? [] : opcoes.map((o) => o.id))} data-teste={`${teste}-todos`}>
          {rascunho.length === opcoes.length ? 'Desmarcar todas' : 'Selecionar todas'}
        </button>
        <button type="button" className="botao-primario pequeno" disabled={!mudou} onClick={() => aoAplicar(ordenar(rascunho))} data-teste={`${teste}-aplicar`}>
          Aplicar
        </button>
      </div>
    </fieldset>
  );
}

/** Filtro por PARCEIRO COMERCIAL (Oportunidades: parceiro selecionado OU cotado na Proposta). */
export function FiltroGrupo({ grupo, setGrupo, parceiros, teste = 'filtro-grupo' }: { grupo: string; setGrupo: (g: string) => void; parceiros: { id: string; nome: string }[]; teste?: string }) {
  return (
    <label>
      Parceiro comercial
      <select value={grupo} onChange={(e) => setGrupo(e.target.value)} data-teste={teste}>
        <option value="">Todos os parceiros</option>
        {parceiros.map((g) => (
          <option key={g.id} value={g.id}>
            {g.nome}
          </option>
        ))}
        <option value="sem">Sem parceiro definido</option>
      </select>
    </label>
  );
}

/* ---------- Selos ---------- */
/** Selo de TIPO: contorno colorido + texto (a cor nunca é o único meio). */
export function SeloTipo({ tipo }: { tipo: Tipo }) {
  return (
    <span className={`selo-tipo selo-tipo-${tipo}`} data-teste="selo-tipo">
      {TIPOS[tipo]}
    </span>
  );
}
const TOM_ESTAGIO: Record<Estagio, Tom> = { prospeccao: 'aberto', qualificacao: 'aberto', proposta_em_elaboracao: 'aberto', proposta_enviada: 'aberto', negociacao: 'aberto', ganha: 'positivo', ganha_parcialmente: 'parcial', perdida: 'encerrado', cancelada: 'encerrado' };
export const tomEstagio = (e: Estagio) => TOM_ESTAGIO[e];

/** Ícone de cada nível (a cor nunca é o único indicador: ícone + nome do nível). */
export const ICONE_NIVEL: Record<NivelRegistro, string> = { valido: '✓', aviso: 'ℹ', atencao: '◆', urgente: '▲', critico: '✖', substituido: '↺', encerrado: '■' };
export function AlertaPrioridade({ nivel, children, teste }: { nivel: NivelRegistro; children?: ReactNode; teste?: string }) {
  return (
    <span className={`alerta-prioridade reg-${nivel}`} data-nivel={nivel} data-teste={teste}>
      <span className="icone-nivel" aria-hidden>
        {ICONE_NIVEL[nivel]}
      </span>{' '}
      <span className="nivel">{NIVEIS[nivel]}</span>
      {children}
    </span>
  );
}
/**
 * Selo do Registro com o nível PROGRESSIVO. `vencimento`: "dias" ("vence em X dia(s)"), "data" ("vence DD/MM/AAAA")
 * ou "oculto" (só o nível). A contagem de dias fica sempre na dica.
 */
export function SeloRegistro({ r, faixas, hoje, curto, vencimento = 'dias' }: { r: RegistroBase; faixas: FaixasRegistro; hoje: string; curto?: boolean; vencimento?: 'dias' | 'data' | 'oculto' }) {
  const s = statusRegistro(r, hoje, faixas);
  const resto = s.texto.startsWith(`${NIVEIS[s.nivel]} — `) ? s.texto.slice(NIVEIS[s.nivel].length) : ` — ${s.texto}`;
  const renovado = r.situacao === 'renovado' ? ' (renovado)' : '';
  const complemento = s.nivel === 'encerrado' ? `em ${dataEncerramentoRegistro(r.encerrado_em)}${r.motivo_encerramento ? ` · ${r.motivo_encerramento}` : ''}` : s.nivel === 'substituido' ? resto.trim() : vencimento === 'dias' ? resto.trim() : vencimento === 'data' ? `— ${s.dias < 0 ? 'venceu em' : 'vence'} ${dataBr(r.data_vencimento)}${renovado}` : renovado.trim();
  return (
    <span className={`selo-registro reg-${s.nivel}`} data-teste="selo-registro" data-nivel={s.nivel} title={`Registro ${r.numero} — ${s.texto} (vencimento ${dataBr(r.data_vencimento)})`}>
      <span className="icone-nivel" aria-hidden>
        {ICONE_NIVEL[s.nivel]}
      </span>{' '}
      <span>{curto ? 'Registro:' : `${r.numero}:`}</span> <span className="nivel">{NIVEIS[s.nivel]}</span>
      {complemento && <span> {complemento}</span>}
    </span>
  );
}
export function LegendaAlertas({ faixas }: { faixas: FaixasRegistro }) {
  return (
    <p className="texto-secundario legenda-alertas" data-teste="legenda-alertas">
      Alerta progressivo do Registro: <AlertaPrioridade nivel="aviso"> — até {faixas[0]} dias</AlertaPrioridade> <AlertaPrioridade nivel="atencao"> — até {faixas[1]} dias</AlertaPrioridade> <AlertaPrioridade nivel="urgente"> — até {faixas[2]} dias</AlertaPrioridade>{' '}
      <AlertaPrioridade nivel="critico"> — vencido</AlertaPrioridade>. Uma única próxima ação por registro: só a prioridade muda.
    </p>
  );
}
